import {
  ApplicationConfig,
  APP_INITIALIZER,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { provideBrnTooltipDefaultOptions } from '@spartan-ng/brain/tooltip';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { appRoutes } from './app.routes';
import { authInterceptor } from './core/auth/auth.interceptor';
import { correlationIdInterceptor } from './core/interceptors/correlation-id.interceptor';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { GlobalErrorHandler } from './core/error/global-error.handler';
import { AuthService } from './core/auth/auth.service';

function initializeAuth(): () => Promise<void> {
  const authService = inject(AuthService);
  return () => authService.configure();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes),
    provideHttpClient(
      withInterceptors([
        correlationIdInterceptor,
        authInterceptor,
        httpErrorInterceptor,
      ])
    ),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideAnimationsAsync(),
    provideOAuthClient(),
    // Theme classes for the BrnTooltip overlay content. Adopted instead of
    // the previous custom hlm-tooltip-bubble CSS — CDK Overlay handles the
    // body-portal positioning we used to do by hand.
    provideBrnTooltipDefaultOptions({
      showDelay: 220,
      hideDelay: 0,
      position: 'top',
      // Readability: cap width + allow wrapping so multi-sentence tooltips render
      // legibly across lines instead of one overflowing nowrap line. text-sm (not
      // text-xs) per the project no-text-xs rule. BrnTooltip exposes no per-instance
      // content-class input, so this global tweak is the only readability lever.
      tooltipContentClasses:
        'rounded-md border border-border bg-popover text-popover-foreground px-2.5 py-1.5 text-sm font-medium shadow-md max-w-xs whitespace-normal pointer-events-none',
      arrowClasses: () => '',
      svgClasses: 'hidden',
    }),
    // Cap every dialog panel to the viewport (minus a 1rem gutter each side).
    // Material defaults the panel max-width to 80vw, which on narrow screens is
    // smaller than a dialog surface's min-width — the surface then overflows the
    // panel and the footer actions get clipped (EB-3856). Pairs with the
    // viewport-capped min-widths in HlmDialogSurfaceComponent.
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { maxWidth: 'calc(100vw - 2rem)' },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true,
    },
  ],
};
